import { useState, useRef, useEffect, useCallback, createContext, useContext, type ReactNode } from "react";

// ── Modal context ─────────────────────────────────────────

interface ModalState {
  confirm: (message: string, options?: { destructive?: boolean }) => Promise<boolean>;
  prompt: (message: string, defaultValue?: string) => Promise<string | null>;
}

const ModalContext = createContext<ModalState | null>(null);

export function useModal(): ModalState {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be inside ModalProvider");
  return ctx;
}

// ── Provider + rendered modal ─────────────────────────────

interface ModalConfig {
  type: "confirm" | "prompt";
  message: string;
  defaultValue?: string;
  destructive?: boolean;
  resolve: (value: any) => void;
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<ModalConfig | null>(null);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (modal?.type === "prompt") {
      setInputValue(modal.defaultValue ?? "");
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [modal]);

  // Close on Escape
  useEffect(() => {
    if (!modal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        modal.resolve(modal.type === "confirm" ? false : null);
        setModal(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [modal]);

  const confirm = useCallback((message: string, options?: { destructive?: boolean }) => {
    return new Promise<boolean>((resolve) => {
      setModal({ type: "confirm", message, destructive: options?.destructive, resolve });
    });
  }, []);

  const prompt = useCallback((message: string, defaultValue?: string) => {
    return new Promise<string | null>((resolve) => {
      setModal({ type: "prompt", message, defaultValue, resolve });
    });
  }, []);

  const handleConfirm = () => {
    if (!modal) return;
    if (modal.type === "confirm") {
      modal.resolve(true);
    } else {
      modal.resolve(inputValue || modal.defaultValue || "");
    }
    setModal(null);
  };

  const handleCancel = () => {
    if (!modal) return;
    modal.resolve(modal.type === "confirm" ? false : null);
    setModal(null);
  };

  return (
    <ModalContext.Provider value={{ confirm, prompt }}>
      {children}
      {modal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div
            className="modal-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <p className="modal-message">{modal.message}</p>
            {modal.type === "prompt" && (
              <input
                ref={inputRef}
                className="modal-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirm(); }}
                autoFocus
              />
            )}
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className={`modal-btn modal-btn-confirm ${modal.destructive ? "modal-btn-destructive" : ""}`}
                onClick={handleConfirm}
                autoFocus={modal.type === "confirm"}
              >
                {modal.type === "confirm" ? "Confirm" : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}
